import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Form } from 'antd'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import useSWR from 'swr'
import CommonFunction from 'utils/commonFunction'

import useTourUploadImage, { TourUploadImagePath } from './useTourUploadImage'

export default function useSettingTourLocation({
  handleCancel
}: {
  handleCancel: () => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { id: buildingId } = useParams()
  const mapRef = useRef<HTMLDivElement>(null)
  const locationUrlValue = Form.useWatch('locationUrl', form)
  const [isValidSharedLink, setIsValidSharedLink] = useState(false)
  const { data: buildingInfo, isLoading } = useSWR<Building>({
    url: `v1/buildings/${buildingId}`
  })

  const { trigger: updateMap, isMutating: updating } = useMutation(
    `v1/buildings/${buildingId}/update-extra-info`
  )

  const { trigger: deleteMap, isMutating: deleting } = useMutation(
    `v1/buildings/${buildingId}/update-extra-info`,
    {},
    true
  )

  const { uploadFileDescription, isUploading } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.LOCATION
  })

  // save location url
  const handleSaveMap = async () => {
    if (!locationUrlValue?.trim()) return

    const file = new File([locationUrlValue], 'location.txt', {
      type: 'text/plain',
      lastModified: new Date().getTime()
    })
    await uploadFileDescription(file, {
      onSuccess: (key) => {
        // update location url to database
        updateMap({
          method: 'patch',
          data: {
            location: key
          },
          successMessage: t('building.saved')
        }).then(() => {
          // closed modal when update logo success
          handleCancel()
        })
      }
    })
  }

  // delete location url
  const handleDeleteTourLocation = () => {
    deleteMap({
      method: 'patch',
      data: {
        location: ''
      },
      successMessage: t('building.deleted')
    }).then(() => {
      // closed modal when update logo success
      handleCancel()
    })
  }

  // validate location url
  useEffect(() => {
    const iframePattern: RegExp =
      /^<iframe\s+src="https:\/\/www\.google\.com\/maps\/embed\?.*?"\s+width="\d+"\s+height="\d+"\s+style="[^"]*"\s+allowfullscreen.*?><\/iframe>$/
    if (iframePattern.test(locationUrlValue)) {
      setIsValidSharedLink(true)
    } else {
      setIsValidSharedLink(false)
    }

    if (mapRef.current && locationUrlValue !== undefined) {
      if (
        locationUrlValue?.trim() !== '' &&
        iframePattern.test(locationUrlValue) &&
        locationUrlValue
      ) {
        mapRef.current.innerHTML = locationUrlValue
      } else {
        mapRef.current.innerHTML = ''
      }
    }
  }, [locationUrlValue])

  // fetch location iframe from s3 link
  useEffect(() => {
    const handleFetchLocationUrl = async () => {
      if (buildingInfo) {
        if (!buildingInfo.extraInfo.locationUrl) return

        const locationUrl =
          await CommonFunction.FetchTourLocationContentFromS3Link(
            buildingInfo.extraInfo.locationUrl
          )

        form.setFieldsValue({
          locationUrl
        })
      }
    }

    handleFetchLocationUrl()
  }, [buildingInfo, form])

  return {
    handleSaveMap,
    handleDeleteTourLocation,
    form,
    mapRef,
    locationUrlValue,
    updating,
    isLoading,
    deleting,
    isValidSharedLink,
    isUploading,
    t
  }
}
